import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import { find, get, intersection, isEmpty, isNil, isNull } from 'lodash';
import moment from 'moment';
import { isAllValuesIsNil, resolveSagisClaim } from 'helpers/utils';
import {
  DEFAULT_INTERVENANT_CODE,
  DEFAULT_LAWYER_CODE_NAME,
  DEFAULT_PROVIDER_CODE,
  HOLDER_PV_LAWYER,
  HOLDER_PV_PROVIDER,
  PROFIL_OPENNER_GJ,
  PROFIL_OPENNER_GO,
  PROFIL_OPENNER_GPO,
  PROFIL_OPENNER_LAWYER,
} from 'containers/RecapPage/canstants';
import {
  getPaymentInfoFeeNote,
  getPaymentForCosts,
} from 'containers/LawyerFeeNote/utils';
import {
  BENEFICIARY,
  CLAIM,
  CLAIM_STATUS,
  DRIVER,
  LISTINGS,
  VALIDATION_DECISION,
  VICTIM,
} from 'containers/data-structure';
import {
  CHOICE,
  INJURY_POST_DECEASED,
} from 'containers/PreOpeningPage/constants';
import {
  DEFAULT_LINK_PROVIDER,
  LIMIT_DATE_INVOICE,
  PROCEDURE_STEPS_VALUES,
} from 'containers/LawyerFeeNote/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import {
  DEFAULT_LAWYER_CODE,
  MANAGEMENT_MODES,
  OPEN_CLAIM_CONSTANTS,
} from 'containers/RecapManagerPage/constants';
import currentUserStorage from 'containers/services/security/current-user-storage';
import {
  getAssignedJurisdictionLawyerCode,
  isClaimClosOrNoAction,
  openingHelper,
  resolveAssignedManager,
} from 'containers/RecapManagerPage/utils';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import messages from 'containers/RecapPage/messages';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import { getValueByCode } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { PROCEDURE_STEPS } from 'containers/LawyerFeeNote/constants';
import { PROFESSION_CODES } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import {
  addPriorityReasonProfession,
  removeWhiteSpaceFromString,
} from 'services/utils';
import { appConfigListingTyping, VictimType } from 'typings/globalTypes';
import { OPENING_STATE, OPENING_TYPE } from 'services/constants';
import { isLawyer } from 'containers/AppContainer/utils';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { formatOpposingParties } from 'containers/CourtManagerLegalProceedings/utils';

export const extractIntervenantType = (
  listings: Listings,
  options: string[] | string | undefined,
) => {
  if (typeof options === 'string') return options;
  if (!options) return DEFAULT_INTERVENANT_CODE;
  let interType: string = DEFAULT_INTERVENANT_CODE;
  let interCode: string = '';
  const intervenantTypes = get(
    listings,
    'intervenantType',
  ).map((el: SagisConfig) => el.ItemDescription.toLowerCase());
  const intervenantType = intersection(options, intervenantTypes);
  if (!isEmpty(intervenantType)) interType = intervenantType[0];
  interCode = get(
    get(listings, 'intervenantType').find(
      (el: SagisConfig) => el.ItemDescription.toLowerCase() === interType,
    ),
    'ItemKey',
    DEFAULT_INTERVENANT_CODE,
  );
  return interCode;
};

export const formatClaimToOpen = (
  claimDetails: any,
  claims: any,
  listings: any,
  isSuspicious: string,
  notificationId: any,
  priorityReasonClaimId: number | null = null,
) => {
  const {
    claim,
    victims,
    opposingParty,
    driver,
    documents,
    fraudInfo,
  } = claimDetails;

  const updatePriorityVictim =
    !victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;

  const resp = find(
    get(listings, 'responsability'),
    (element: { ItemKey: any }) =>
      element.ItemKey === claimDetails[VALIDATION_DECISION.RESPONSIBILITY],
  );
  const responsibility =
    claimDetails[VALIDATION_DECISION.MANAGEMENT_MODE] === '2'
      ? claim[CLAIM.RESPONSABILITY_SHARE]
      : get(resp, 'ItemDescription', claim[CLAIM.RESPONSABILITY_SHARE]);
  const lawyer = find(
    get(listings, 'intervenants'),
    (element: { ItemKey: any; ItemDescription: string }) =>
      element.ItemDescription === DEFAULT_LAWYER_CODE_NAME,
  );
  let otherDriver = driver;
  let formattedDriver = driver;
  let formattedVehicule = [];
  let formattedOpposingParties = [];
  if (driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.YES) {
    otherDriver = {};
    formattedDriver = {};
  }

  if (!isEmpty(driver) && driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.NO) {
    otherDriver = {
      ...driver,
      isDriverTheInsured:
        get(driver, 'isDriverTheInsured') === CHOICE.NO
          ? false
          : Boolean(get(driver, 'isDriverTheInsured')),
      driverFirstName: get(driver, [DRIVER.FIRST_NAME], ''),
      driverLastName: get(driver, [DRIVER.LAST_NAME], ''),
      driverLicenceDate: get(driver, [DRIVER.LICENSE_START_DATE], null),
      driverLicenceNumber: get(driver, [DRIVER.LICENSE_ID], null),
    };
  }

  if (featureService.hasFeature(FEATURES.OPPOSING_PARTY)) {
    formattedOpposingParties = opposingParty.vehicules.map(
      (vehicule: Vehicule) => openingHelper.formatSagisVehiculeObject(vehicule),
    );
    formattedVehicule = opposingParty.vehicules;
  }

  const formattedVictims = victims.map((victim: VictimType) => {
    const getDescriptionByCode = (code: number | string, list: any) => {
      const item = get(list, 'classVictim', []).find(
        (item: SagisConfig) => Number(item.ItemKey) === Number(code),
      );
      return item ? item.ItemDescription : '';
    };
    let setDefaultBirthDate = false;
    if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
      setDefaultBirthDate = true;
    }
    const commonVictim: any = {
      victimFirstName: victim[VICTIM.FIRST_NAME],
      victimLastName: victim[VICTIM.LAST_NAME],
      victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
      injuryCode:
        String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
          ? victim[VICTIM.INJURY_POST_KEY]
          : 0,
      qualityVictim: victim[VICTIM.QUALITY_KEY],
      classVictim: victim[VICTIM.CLASS_KEY],
      classVictimDescription: getDescriptionByCode(
        victim[VICTIM.CLASS_KEY],
        listings,
      ),
      Town: victim[VICTIM.CITY],
      Complement: victim[VICTIM.ADDRESS],
      isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1,
      setDefaultBirthDate,
      itt: parseInt((victim[VICTIM.ITT] as unknown) as string),
      gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
      cspCode: get(
        get(listings, 'Profession', []).find(
          (el: any) => el.ItemKey === victim.professionCode,
        ),
        'ItemGroup',
        null,
      ),
      numberOfSpouse: victim[VICTIM.NUMBER_OF_SPOUSE],
      annualInCome:
        ((victim.annualOfIncome as unknown) as string) === ''
          ? undefined
          : victim.annualOfIncome,
      annualOfIncome:
        ((victim.annualOfIncome as unknown) as string) === ''
          ? null
          : victim.annualOfIncome,
    };
    if (victim.injuryPostKey === INJURY_POST_DECEASED) {
      victim.nextOfKins = get(victim, 'beneficiaries', []).map((el: any) => {
        return {
          fullName: `${removeWhiteSpaceFromString(
            el.firstName,
          )} ${removeWhiteSpaceFromString(el.lastName)}`,
          qualityCode: Number(el.beneficiaryType),
          age: el.age,
          birthDate: el?.dateOfBirth,
          funeralFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
            ? el.funeralFees
            : !isNil(el.funeralFees) && !!Number(responsibility)
            ? el.funeralFees / (Number(responsibility) / 100)
            : el.funeralFees,
          handicapIndicator: el.handicapIndicator ? 'Y' : 'N',
          dependentIndicator: el.dependentIndicator ? 'Y' : 'N',
        };
      });
      delete commonVictim.itt;
      delete (victim as Partial<VictimType>).itt;
    } else {
      delete (victim as Partial<VictimType>).beneficiaries;
    }

    return {
      ...victim,
      ...commonVictim,
    };
  });

  const managerAssignedCode = resolveAssignedManager(
    get(claimDetails, 'claim.lossLocation', ''),
    get(claimDetails, 'claim.lossDate', ''),
  );

  const date = new Date(get(claimDetails, 'claim.lossDate'));
  const dayOfMonth = date.getDate();

  const lawyerJurisdictionAssignedCode = getAssignedJurisdictionLawyerCode(
    get(listings, LISTINGS.MAPPING_LAWYER_JURISDICTION, []),
    get(claimDetails, 'claim.jurisdictionId'),
    dayOfMonth,
  );

  return {
    ClaimNumber: !isEmpty(claims) ? claims.code : null,
    declarant: OPEN_CLAIM_CONSTANTS.DECLARANT_LAWYER_CODE,
    countryId: claim[CLAIM.COUNTRY_ID],
    receptionDate: moment().format('DD/MM/YYYY'),
    resp: responsibility,
    policyNumber: claim[CLAIM.POLICY_NUMBER],
    lossDate: moment(claim[CLAIM.LOSS_DATE]).format('DD/MM/YYYY'),
    lossTime: claim[CLAIM.LOSS_HOUR],
    placeOfOccurenceId: claim[CLAIM.LOSS_LOCATION],
    comments: claimDetails[VALIDATION_DECISION.COMMENT],
    withrisk: true,
    isFromCorporel: true,
    hasInjuredPeople: true,
    isPta: false,
    isMT: false,
    hasMaterialDamaged: false,
    procedureCode: '',
    pvNumber: claim[CLAIM.PV_NUMBER],
    isClosOrNoAction: isClaimClosOrNoAction(claims),
    risques: [
      {
        claimKey: !isEmpty(claims) ? claims.code : null,
        scaleCaseCode: claim[CLAIM.SCALE_CASE_KEY],
        circumstanceCode: claim[CLAIM.ACCIDENT_CIRCONSTANCE_KEY],
        circumstance: claim[CLAIM.ACCIDENT_CIRCONSTANCE],
        provisionHistory: [],
        riskId: '',
        managementMode: claim[CLAIM.MANAGEMENT_MODE],
        responsabilityRate: claim[CLAIM.RESPONSABILITY_KEY],
        shouldSendNotification: false,
        victims: formattedVictims,
        opposingParties: formattedOpposingParties,
        isFromCorporel: true,
        lossDate: !isEmpty(claims)
          ? moment(claim[CLAIM.LOSS_DATE]).format('DD/MM/YYYY')
          : undefined,
      },
    ],
    otherDriver: !isEmpty(otherDriver) ? otherDriver : undefined,
    vehicules: formattedVehicule,
    victims: victims.map((vic: any) => {
      if (vic['injuryPostKey'] !== '2') vic.beneficiaries = [];
      vic.beneficiaries = !isEmpty(vic.beneficiaries)
        ? vic.beneficiaries.map((el: any) => {
            return {
              ...el,
              [BENEFICIARY.FIRST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.FIRST_NAME, ''),
              ),
              [BENEFICIARY.LAST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.LAST_NAME, ''),
              ),
              [BENEFICIARY.FRAIS]: Number(el[BENEFICIARY.FRAIS]),
            };
          })
        : [];
      return {
        ...vic,
        [VICTIM.ITT]: vic.itt === '' ? undefined : vic.itt,
        [VICTIM.ANNUAL_INCOME]:
          vic.annualOfIncome === '' ? undefined : vic.annualOfIncome,
        [VICTIM.IS_EXCLUDED]: vic[VICTIM.IS_EXCLUDED] === CHOICE.YES,
      };
    }),
    claim: {
      ...claim,
      profilOpenner: PROFIL_OPENNER_GO,
      [CLAIM.POLICY_NUMBER]: get(claims, 'policyNumber', ''),
      suspicious: claim[CLAIM.SUSPICIOUS] === CHOICE.YES ? true : false,
      isNatureChanged: true,
      decidedAt: moment().format(),
      fraudInfo:
        featureService.hasFeature(FEATURES.FRAUD_INFO) &&
        claim[CLAIM.SUSPICIOUS] === CHOICE.YES
          ? {
              ...fraudInfo,
              createdBy: currentUserStorage.get()
                ? currentUserStorage.get()?.username
                : null,
            }
          : undefined,
      decidedBy: currentUserStorage.get()
        ? currentUserStorage.get().username.toLowerCase()
        : null,
      status: CLAIM_STATUS.VALIDATED,
      priorityReasonId: addPriorityReasonProfession(
        null as any,
        !!victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
        listings,
      )
        ? addPriorityReasonProfession(
            null as any,
            !!victims.find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            listings,
          )
        : updatePriorityVictim
        ? null
        : undefined,
      createdBy: currentUserStorage.get()
        ? currentUserStorage.get().username.toLowerCase()
        : null,
      updatedBy: currentUserStorage.get()
        ? currentUserStorage.get().username.toLowerCase()
        : null,
      codeLawyer: get(lawyer, 'ItemKey', ''),
    },
    documents,
    driver: otherDriver,
    jurisdictionDegreeId: 1,
    procedureNatureId: 3,
    notificationId: notificationId,
    fflipFeeNote: featureService.hasFeature(FEATURES.FEE_NOTE),
    feeNoteSagisProvision: {
      InterKey: get(lawyer, 'ItemKey', ''), // l’id de l’avocat (à récupérer du listing sagis api)
      Name: get(lawyer, 'ItemDescription', ''),
      TypeCde: DEFAULT_INTERVENANT_CODE,
      ReasonCode: '2', // 2 pour Evaluation système, il faut préciser avec le métier le motif à mettre par défaut
      Amount: Number(3020),
    },
    lawyerJurisdictionAssignedCode,
    managerAssignedCode,
    isAutomaticLawyerAssignment: featureService.hasFeature(
      FEATURES.ASSIGNMENT_JURISDICTION_LAWYER,
    ),
    isTransactionInboxesActive: featureService.hasFeature(
      FEATURES.INBOXES_TRANSACTION_MANAGER,
    ),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    assignedManagerName: get(
      get(listings, LISTINGS.ASSIGNMENT_MANAGER, []).find(
        (el: any) => el.id === managerAssignedCode,
      ),
      'name',
      '',
    ),
    assignedLawyerName: getValueByCode(
      lawyerJurisdictionAssignedCode,
      get(listings, LISTINGS.STACKHOLDERS, []),
    ),
    fraudInfo:
      featureService.hasFeature(FEATURES.FRAUD_INFO) &&
      claim[CLAIM.SUSPICIOUS] === CHOICE.YES
        ? {
            ...fraudInfo,
            createdBy: currentUserStorage.get()
              ? currentUserStorage.get()?.username
              : null,
          }
        : undefined,
    claimId: get(claimDetails, 'claim.id'),
    intervenantTypeCode: get(claimDetails, 'claim.intervenantTypeCode'),
    intervenantCode: get(claimDetails, 'claim.codeLawyer'),
    intervenantSagis: [
      {
        intervenantTypeCode: DEFAULT_INTERVENANT_CODE,
        intervenantCode: lawyerJurisdictionAssignedCode,
      },
    ],
    isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
  };
};

export const formatClaimToOpenGpo = (
  claimDetails: any,
  pvHolderType: any,
  firmReferenceLawyer: any,
  completionDate: any,
  receptionDate: any,
  otherPvHolder: any,
  providerTo: any,
  invoiceInfo: any,
  invoiceInfoAdditionelFees: any,
  claims: any,
  listings: any,
  isSuspicious: string,
  policyInfo: any,
  isMixedClaim: boolean,
  cost: any,
  priorityReasonClaimId: number | null = null,
) => {
  const {
    claim,
    victims,
    opposingParty,
    driver,
    documents,
    fraudInfo,
  } = claimDetails;

  const updatePriorityVictim =
    !victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;

  const resp = find(
    get(listings, 'responsability'),
    (element: { ItemKey: any }) =>
      element.ItemKey === claimDetails[VALIDATION_DECISION.RESPONSIBILITY],
  );
  const responsibility =
    claimDetails[VALIDATION_DECISION.MANAGEMENT_MODE] === '2'
      ? claim[CLAIM.RESPONSABILITY_SHARE]
      : get(resp, 'ItemDescription', claim[CLAIM.RESPONSABILITY_SHARE]);
  const lawyer = find(
    get(listings, 'intervenants'),
    (element: { ItemKey: any; ItemDescription: string }) =>
      element.ItemDescription === DEFAULT_LAWYER_CODE_NAME,
  );
  const providerSagisInfo = find(
    get(listings, 'intervenants'),
    (element: { ItemKey: any; ItemDescription: string }) =>
      element.ItemKey === providerTo,
  );
  let otherDriver = driver;
  let formattedDriver = driver;
  let formattedVehicule = [];
  let formattedOpposingParties = [];
  if (driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.YES) {
    otherDriver = {};
    formattedDriver = {};
  }

  if (!isEmpty(driver) && driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.NO) {
    otherDriver = {
      ...driver,
      isDriverTheInsured:
        get(driver, 'isDriverTheInsured') === CHOICE.NO
          ? false
          : Boolean(get(driver, 'isDriverTheInsured')),
      driverFirstName: get(driver, [DRIVER.FIRST_NAME], ''),
      driverLastName: get(driver, [DRIVER.LAST_NAME], ''),
      driverLicenceDate: get(driver, [DRIVER.LICENSE_START_DATE], null),
      driverLicenceNumber: get(driver, [DRIVER.LICENSE_ID], null),
    };
  }

  if (featureService.hasFeature(FEATURES.OPPOSING_PARTY)) {
    formattedOpposingParties = opposingParty.vehicules.map(
      (vehicule: Vehicule) => openingHelper.formatSagisVehiculeObject(vehicule),
    );
    formattedVehicule = opposingParty.vehicules;
  }

  const formattedVictims = victims.map((victim: VictimType) => {
    const getDescriptionByCode = (code: number | string, list: any) => {
      const item = get(list, 'classVictim').find(
        (item: any) => Number(item.ItemKey) === Number(code),
      );
      return item ? item.ItemDescription : '';
    };
    let setDefaultBirthDate = false;
    if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
      setDefaultBirthDate = true;
    }
    const commonVictim: any = {
      victimFirstName: victim[VICTIM.FIRST_NAME],
      victimLastName: victim[VICTIM.LAST_NAME],
      victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
      injuryCode:
        String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
          ? victim[VICTIM.INJURY_POST_KEY]
          : 0,
      qualityVictim: victim[VICTIM.QUALITY_KEY],
      classVictim: victim[VICTIM.CLASS_KEY],
      classVictimDescription: getDescriptionByCode(
        victim[VICTIM.CLASS_KEY],
        listings,
      ),
      cspCode: get(
        get(listings, 'Profession', []).find(
          (el: any) => el.ItemKey === victim.professionCode,
        ),
        'ItemGroup',
        null,
      ),
      gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
      annualInCome:
        ((victim.annualOfIncome as unknown) as string) === ''
          ? undefined
          : victim.annualOfIncome,
      annualOfIncome:
        ((victim.annualOfIncome as unknown) as string) === ''
          ? null
          : victim.annualOfIncome,
      Town: victim[VICTIM.CITY],
      Complement: victim[VICTIM.ADDRESS],
      isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1,
      setDefaultBirthDate,
      itt: parseInt((victim[VICTIM.ITT] as unknown) as string),
      numberOfSpouse: victim[VICTIM.NUMBER_OF_SPOUSE],
    };
    if (victim.injuryPostKey === INJURY_POST_DECEASED) {
      victim.nextOfKins = victim.beneficiaries.map((el: any) => {
        return {
          fullName: `${removeWhiteSpaceFromString(
            el.firstName,
          )} ${removeWhiteSpaceFromString(el.lastName)}`,
          qualityCode: Number(el.beneficiaryType),
          age: el.age,
          birthDate: el?.dateOfBirth,
          funeralFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
            ? el.funeralFees
            : !isNil(el.funeralFees) && !!Number(responsibility)
            ? el.funeralFees / (Number(responsibility) / 100)
            : el.funeralFees,
          handicapIndicator: el.handicapIndicator ? 'Y' : 'N',
          dependentIndicator: el.dependentIndicator ? 'Y' : 'N',
        };
      });
      delete commonVictim.itt;
      delete (victim as Partial<VictimType>).itt;
    } else {
      delete (victim as Partial<VictimType>).beneficiaries;
    }

    return {
      ...victim,
      ...commonVictim,
      isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
    };
  });

  const managerAssignedCode = resolveAssignedManager(
    get(claimDetails, 'claim.lossLocation', ''),
    get(claimDetails, 'claim.lossDate', ''),
  );

  const date = new Date(get(claimDetails, 'claim.lossDate'));
  const dayOfMonth = date.getDate();

  const lawyerJurisdictionAssignedCode = getAssignedJurisdictionLawyerCode(
    get(listings, LISTINGS.MAPPING_LAWYER_JURISDICTION, []),
    get(claimDetails, 'claim.jurisdictionId'),
    dayOfMonth,
  );
  const diffPvDates =
    new Date(receptionDate).valueOf() - new Date(completionDate).valueOf();
  const daysOfDiff = Math.ceil(diffPvDates / (1000 * 60 * 60 * 24));
  const sagisInvoiceInfo =
    !isNil(get(invoiceInfo, 'invoiceNumber', null)) &&
    !isNil(get(invoiceInfo, 'invoiceDate', null)) &&
    !isNil(get(invoiceInfo, 'invoiceReceptionDate', null))
      ? {
          ...invoiceInfo,
          invoiceNumber: get(invoiceInfo, 'invoiceNumber', null),
          invoiceDate:
            get(invoiceInfo, 'invoiceDate', null) === 'Invalid date'
              ? null
              : get(invoiceInfo, 'invoiceDate', null),
          receptionDate:
            get(invoiceInfo, 'invoiceReceptionDate', null) === 'Invalid date'
              ? null
              : get(invoiceInfo, 'invoiceReceptionDate', null),
          InvoiceDate: get(invoiceInfo, 'invoiceDate'),
          InvoiceReceptionDate: get(invoiceInfo, 'receptionDate'),
        }
      : {};
  const sagisInvoiceInfoAdditionelFees =
    !isNil(get(invoiceInfoAdditionelFees, 'invoiceNumber', null)) &&
    !isNil(get(invoiceInfoAdditionelFees, 'invoiceDate', null)) &&
    !isNil(get(invoiceInfoAdditionelFees, 'invoiceReceptionDate', null))
      ? {
          ...invoiceInfoAdditionelFees,
          invoiceNumber: get(invoiceInfoAdditionelFees, 'invoiceNumber', null),
          invoiceDate:
            get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
            'Invalid date'
              ? null
              : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
          receptionDate:
            get(invoiceInfoAdditionelFees, 'invoiceReceptionDate', null) ===
            'Invalid date'
              ? null
              : get(invoiceInfoAdditionelFees, 'invoiceReceptionDate', null),
          InvoiceDate: get(invoiceInfoAdditionelFees, 'invoiceDate'),
          InvoiceReceptionDate: get(invoiceInfoAdditionelFees, 'receptionDate'),
        }
      : {};
  return {
    ClaimNumber: !isEmpty(claims) ? claims.code : null,
    declarant: OPEN_CLAIM_CONSTANTS.DECLARANT_LAWYER_CODE,
    countryId: claim[CLAIM.COUNTRY_ID],
    receptionDate: moment().format('DD/MM/YYYY'),
    resp: responsibility,
    policyNumber: get(policyInfo, 'sagis.policy.policyNumber', ''),
    cin: get(policyInfo, 'sagis.policy.CIN', ''),
    lossDate: moment(claim[CLAIM.LOSS_DATE]).format('DD/MM/YYYY'),
    lossTime: claim[CLAIM.LOSS_HOUR],
    placeOfOccurenceId: claim[CLAIM.LOSS_LOCATION],
    comments: claimDetails[VALIDATION_DECISION.COMMENT],
    withrisk: true,
    isFromCorporel: true,
    hasInjuredPeople: true,
    isPta: false,
    isMT: false,
    isMixedClaim: isMixedClaim, //to-update
    hasMaterialDamaged: false,
    procedureCode: '',
    pvNumber: claim[CLAIM.PV_NUMBER],
    isClosOrNoAction: isClaimClosOrNoAction(claims),
    risques: [
      {
        claimKey: !isEmpty(claims) ? claims.code : null,
        scaleCaseCode: claim[CLAIM.SCALE_CASE_KEY],
        circumstanceCode: claim[CLAIM.ACCIDENT_CIRCONSTANCE_KEY],
        circumstance: claim[CLAIM.ACCIDENT_CIRCONSTANCE],
        provisionHistory: [],
        riskId: '',
        managementMode: claim[CLAIM.MANAGEMENT_MODE],
        responsabilityRate: claim[CLAIM.RESPONSABILITY_KEY],
        shouldSendNotification: false,
        victims: formattedVictims.filter((v: any) => !v.isDeleted),
        opposingParties: formattedOpposingParties,
        isFromCorporel: true,
        lossDate: !isEmpty(claims)
          ? moment(claim[CLAIM.LOSS_DATE]).format('DD/MM/YYYY')
          : undefined,
      },
    ],
    otherDriver: !isEmpty(otherDriver) ? otherDriver : undefined,
    vehicules: formattedVehicule,
    victims: victims.map((vic: any) => {
      if (vic['injuryPostKey'] !== '2') vic.beneficiaries = [];
      vic.beneficiaries = !isEmpty(vic.beneficiaries)
        ? vic.beneficiaries.map((el: any) => {
            return {
              ...el,
              [BENEFICIARY.FIRST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.FIRST_NAME, ''),
              ),
              [BENEFICIARY.LAST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.LAST_NAME, ''),
              ),
              [BENEFICIARY.FRAIS]: Number(el[BENEFICIARY.FRAIS]),
            };
          })
        : [];
      return {
        ...vic,
        [VICTIM.ITT]: vic.itt === '' ? undefined : vic.itt,
        [VICTIM.ANNUAL_INCOME]:
          vic.annualOfIncome === '' ? undefined : vic.annualOfIncome,
        [VICTIM.IS_EXCLUDED]: vic[VICTIM.IS_EXCLUDED] === CHOICE.YES,
      };
    }),
    claim: {
      ...claim,
      policyNumber: get(policyInfo, 'sagis.policy.policyNumber', ''),
      priorityReasonId: addPriorityReasonProfession(
        null as any,
        !!victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
        listings,
      )
        ? addPriorityReasonProfession(
            null as any,
            !!victims.find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            listings,
          )
        : updatePriorityVictim
        ? null
        : undefined,
      cin: get(policyInfo, 'sagis.policy.CIN', ''),
      [CLAIM.FIRM_REFERENCE]:
        pvHolderType === HOLDER_PV_LAWYER
          ? firmReferenceLawyer
          : get(claim, [CLAIM.FIRM_REFERENCE]),
      profilOpenner: PROFIL_OPENNER_GPO,
      codeLawyer:
        pvHolderType === HOLDER_PV_PROVIDER || pvHolderType === HOLDER_PV_LAWYER
          ? providerTo
          : DEFAULT_LAWYER_CODE,
      [CLAIM.INTERVENANT_TYPE]:
        pvHolderType === HOLDER_PV_PROVIDER
          ? DEFAULT_PROVIDER_CODE
          : DEFAULT_INTERVENANT_CODE,
      completionDatePv: !isNil(completionDate)
        ? moment(completionDate).format('YYYY-MM-DD')
        : null,
      receptionDatePv: !isNil(receptionDate)
        ? moment(receptionDate).format('YYYY-MM-DD')
        : null,
      suspicious: claim[CLAIM.SUSPICIOUS] === CHOICE.YES ? true : false,
      isNatureChanged: isMixedClaim,
      fraudInfo:
        featureService.hasFeature(FEATURES.FRAUD_INFO) &&
        claim[CLAIM.SUSPICIOUS] === CHOICE.YES
          ? {
              ...fraudInfo,
              createdBy: currentUserStorage.get()
                ? currentUserStorage.get()?.username
                : null,
            }
          : undefined,
      decidedAt: moment().format(),
      decidedBy: currentUserStorage.get()
        ? currentUserStorage.get().username.toLowerCase()
        : null,
      status: CLAIM_STATUS.VALIDATED,
      createdBy: currentUserStorage.get()
        ? currentUserStorage.get().username.toLowerCase()
        : null,
      updatedBy: currentUserStorage.get()
        ? currentUserStorage.get().username.toLowerCase()
        : null,
    },
    documents,
    driver: otherDriver,
    jurisdictionDegreeId: 1,
    procedureNatureId: 3,
    notificationId: undefined,
    openClaim: Boolean(
      Number(
        get(
          get(listings, 'appConfig', []).find(
            (item: appConfigListingTyping) =>
              item.task === 'ACTIVATE_OPEN_CLAIM_GPO',
          ),
          'value',
          0,
        ),
      ),
    ),
    additionalFees:
      !isNil(get(cost, 'costs', null)) && pvHolderType === HOLDER_PV_PROVIDER
        ? {
            ...cost,
            codeIntervenant: providerTo,
            step: PROCEDURE_STEPS.PV,
            beneficiary: get(
              get(listings, 'intervenants', [])?.find(
                (el: any) => el.ItemKey === providerTo,
              ),
              'ItemDescription',
            ),
            createdBy: currentUserStorage?.get()?.username,
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: providerTo,
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? null
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
            ...sagisInvoiceInfoAdditionelFees,
            contractorCode: providerTo,
            claimExtCode: !isEmpty(claims) ? claims.code : null,
            contractorTypeCode: DEFAULT_PROVIDER_CODE,
            Amount: get(cost, 'costs', null),
            beneficiaryReference: `${PROCEDURE_STEPS_VALUES['PV']}`,
            updateDate: new Date(),
            townCode: get(providerSagisInfo, 'townCode', ''),
            address: get(providerSagisInfo, 'address', ''),
            linkToBeneficiaryCode: DEFAULT_LINK_PROVIDER,
            judgementDate: moment(receptionDate).format('YYYY-MM-DD'),
            ...getPaymentForCosts(listings, {
              ...cost,
              step: PROCEDURE_STEPS.PV,
            }),
            documentId: 88,
            isInboxesActive: featureService.hasFeature(
              FEATURES.ATTRIBUTION_INBOXES_MANAGER,
            ),
            isTrackingActive: featureService.hasFeature(
              FEATURES.TRACKING_ACTIONS_USER,
            ),
            isStatusManagement: featureService.hasFeature(
              FEATURES.STATUS_MANAGEMENT,
            ),
          }
        : undefined,
    fflipFeeNote: featureService.hasFeature(FEATURES.FEE_NOTE),
    feeNote:
      pvHolderType === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: providerTo,
            beneficiary: get(
              get(listings, 'intervenants', [])?.find(
                (el: any) => el.ItemKey === providerTo,
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(invoiceInfo, 'invoiceNumber', null),
                invoiceDate: get(invoiceInfo, '.invoiceDate', null),
                receptionDate: get(invoiceInfo, 'invoiceReceptionDate', null),
                url: get(invoiceInfo, 'url', null),
              })
                ? {
                    ...invoiceInfo,
                    codeIntervenant: providerTo,
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(invoiceInfo, 'invoiceNumber', null),
                    invoiceDate:
                      get(invoiceInfo, 'invoiceDate', null) === 'Invalid date'
                        ? null
                        : get(invoiceInfo, 'invoiceDate', null),
                    receptionDate:
                      get(invoiceInfo, 'invoiceReceptionDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfo, 'invoiceReceptionDate', null),
                  }
                : undefined,
          }
        : undefined,
    feeNoteSagisProvision:
      pvHolderType === HOLDER_PV_PROVIDER
        ? {
            InterKey: get(providerSagisInfo, 'ItemKey', ''), // l’id de l’avocat (à récupérer du listing sagis api)
            Name: get(providerSagisInfo, 'ItemDescription', ''),
            TypeCde: DEFAULT_PROVIDER_CODE,
            ReasonCode: '2', // 2 pour Evaluation système, il faut préciser avec le métier le motif à mettre par défaut
            Amount: Number(200),
          }
        : {
            InterKey: get(lawyer, 'ItemKey', ''), // l’id de l’avocat (à récupérer du listing sagis api)
            Name: get(lawyer, 'ItemDescription', ''),
            TypeCde: DEFAULT_INTERVENANT_CODE,
            ReasonCode: '2', // 2 pour Evaluation système, il faut préciser avec le métier le motif à mettre par défaut
            Amount: Number(0),
          },
    pvHolder: {
      pvHolderTypeId: pvHolderType,
      other: otherPvHolder,
    },
    lawyerJurisdictionAssignedCode,
    managerAssignedCode,
    isAutomaticLawyerAssignment: featureService.hasFeature(
      FEATURES.ASSIGNMENT_JURISDICTION_LAWYER,
    ),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    assignedManagerName: get(
      get(listings, LISTINGS.ASSIGNMENT_MANAGER, []).find(
        (el: any) => el.id === managerAssignedCode,
      ),
      'name',
      '',
    ),
    assignedLawyerName: getValueByCode(
      lawyerJurisdictionAssignedCode,
      get(listings, LISTINGS.STACKHOLDERS, []),
    ),
    claimId: get(claimDetails, 'claim.id'),
    intervenantTypeCode: get(claimDetails, 'claim.intervenantTypeCode'),
    intervenantCode: get(claimDetails, 'claim.codeLawyer'),
    intervenantSagis: [
      {
        intervenantTypeCode: DEFAULT_INTERVENANT_CODE,
        intervenantCode: lawyerJurisdictionAssignedCode,
      },
    ],
    isTransactionInboxesActive: featureService.hasFeature(
      FEATURES.INBOXES_TRANSACTION_MANAGER,
    ),
    fraudInfo:
      featureService.hasFeature(FEATURES.FRAUD_INFO) &&
      claim[CLAIM.SUSPICIOUS] === CHOICE.YES
        ? {
            ...fraudInfo,
            createdBy: currentUserStorage.get()
              ? currentUserStorage.get()?.username
              : null,
          }
        : undefined,
    feeNoteToValidate:
      pvHolderType === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: providerTo,
            beneficiary: get(
              get(listings, 'intervenants', [])?.find(
                (el: any) => el.ItemKey === providerTo,
              ),
              'ItemDescription',
            ),
            createdBy: currentUserStorage?.get()?.username,
            ...sagisInvoiceInfo,
            contractorCode: providerTo,
            claimExtCode: !isEmpty(claims) ? claims.code : null,
            contractorTypeCode: DEFAULT_PROVIDER_CODE,
            beneficiaryReference: `${PROCEDURE_STEPS_VALUES['PV']}`,
            updateDate: new Date(),
            townCode: get(providerSagisInfo, 'townCode', ''),
            address: get(providerSagisInfo, 'address', ''),
            linkToBeneficiaryCode: DEFAULT_LINK_PROVIDER,
            judgementDate: moment(receptionDate).format('YYYY-MM-DD'),
            ...getPaymentInfoFeeNote(listings),
            documentId: 52,
            isInboxesActive: featureService.hasFeature(
              FEATURES.ATTRIBUTION_INBOXES_MANAGER,
            ),
            isTrackingActive: featureService.hasFeature(
              FEATURES.TRACKING_ACTIONS_USER,
            ),
            isStatusManagement: featureService.hasFeature(
              FEATURES.STATUS_MANAGEMENT,
            ),
          }
        : undefined,
    isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
  };
};

export const getClaimDossier = (
  props: any,
  withFeeNote: boolean,
  priorityReasonClaimId: number | null = null,
) => {
  const {
    claim,
    victims,
    documents,
    driver,
    vehicule,
    opposingParty,
    fraudInfo,
  } = props.storedClaimToPreopen;
  const invoiceInfoAdditionelFees = props.invoiceInfoAdditionelFees;
  const updatePriorityVictim =
    !victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;
  let formattedDriver = driver;
  let formattedVehicule = [vehicule];

  if (driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.YES) {
    formattedDriver = {};
  }

  if (claim[CLAIM.HAS_MATERIAL_DAMAGES] === CHOICE.NO) {
    formattedVehicule = [];
  }

  if (claim[CLAIM.IS_JUDICIAL_PROCEDURE] === CHOICE.NO) {
    claim[CLAIM.JUDICIAL_PROCEDURE_ID] = null;
  }
  return {
    claim: {
      ...claim,
      [CLAIM.POLICY_NUMBER]: get(
        props.policyInfo,
        'sagis.policy.policyNumber',
        '',
      ),
      [CLAIM.AUTHORITY_TYPE]: !isNil(get(claim, [CLAIM.AUTHORITY_TYPE], null))
        ? get(claim, [CLAIM.AUTHORITY_TYPE])
        : null,
      [CLAIM.AUTHORITY_NAME]: !isEmpty(get(claim, [CLAIM.AUTHORITY_NAME]))
        ? get(claim, [CLAIM.AUTHORITY_NAME])
        : null,
      [CLAIM.OPENING_TYPE]: props.isUsageOpening
        ? OPENING_TYPE.USAGE
        : OPENING_TYPE.NORMAL,
      [CLAIM.OPENING_STATE]: props.isUsageOpening
        ? OPENING_STATE.NOT_COMPLETED
        : OPENING_STATE.COMPLETED,
      [CLAIM.CIN]: get(props.policyInfo, 'sagis.policy.CIN', ''),
      [CLAIM.STATUS]: CLAIM_STATUS.PENDING,
      fraudInfo:
        featureService.hasFeature(FEATURES.FRAUD_INFO) &&
        claim[CLAIM.SUSPICIOUS] === CHOICE.YES
          ? {
              ...fraudInfo,
              intervenantCode: currentIntervenantStorage?.get()?.sagis_code,
            }
          : undefined,
      [CLAIM.FIRM_REFERENCE]:
        get(props, 'isPreOpeningManager', false) &&
        get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
          ? get(props, 'firmReferenceLawyer', null)
          : get(claim, [CLAIM.FIRM_REFERENCE]),
      profilOpenner: get(props, 'isPreOpeningManager', false)
        ? PROFIL_OPENNER_GPO
        : PROFIL_OPENNER_LAWYER,
      codeLawyer: get(props, 'isPreOpeningManager', false)
        ? get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER ||
          get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
          ? get(props, 'providerTo')
          : DEFAULT_LAWYER_CODE
        : currentIntervenantStorage?.get()?.sagis_code,
      [CLAIM.INTERVENANT_TYPE]: get(props, 'isPreOpeningManager', false)
        ? get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER
          ? DEFAULT_PROVIDER_CODE
          : DEFAULT_INTERVENANT_CODE
        : extractIntervenantType(
            props.listings,
            currentIntervenantStorage?.get()?.roles,
          ),
      completionDatePv: !isNil(get(props, 'completionDate', null))
        ? moment(get(props, 'completionDate', null)).format('YYYY-MM-DD')
        : null,
      receptionDatePv: !isNil(get(props, 'receptionDate', null))
        ? moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD')
        : null,
      priorityReasonId: addPriorityReasonProfession(
        null as any,
        !!victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
        props.listings,
      )
        ? addPriorityReasonProfession(
            null as any,
            !!victims.find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            props.listings,
          )
        : updatePriorityVictim
        ? null
        : undefined,
    },
    victims: !isEmpty(victims)
      ? victims.map((vic: any) => {
          if (vic['injuryPostKey'] !== '2') vic.beneficiaries = [];

          vic.beneficiaries = vic.beneficiaries.map((el: any) => {
            return {
              ...el,
              [BENEFICIARY.FIRST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.FIRST_NAME, ''),
              ),
              [BENEFICIARY.LAST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.LAST_NAME, ''),
              ),
              [BENEFICIARY.FRAIS]: isNull(el[BENEFICIARY.FRAIS])
                ? null
                : Number(el[BENEFICIARY.FRAIS]),
            };
          });
          return {
            ...vic,
            [VICTIM.ITT]: vic.itt === '' ? undefined : vic.itt,
            [VICTIM.ANNUAL_INCOME]:
              vic.annualOfIncome === '' ? undefined : vic.annualOfIncome,
            [VICTIM.IS_EXCLUDED]: vic[VICTIM.IS_EXCLUDED] === CHOICE.YES,
          };
        })
      : [],
    documents,
    driver: formattedDriver,
    vehicules: featureService.hasFeature(FEATURES.OPPOSING_PARTY)
      ? opposingParty.vehicules
      : formattedVehicule,
    feeNote: get(props, 'isPreOpeningManager', false)
      ? get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(props, 'invoiceInfo.invoiceNumber', null),
                invoiceDate: get(props, 'invoiceInfo.invoiceDate', null),
                receptionDate: get(
                  props,
                  'invoiceInfo.invoiceReceptionDate',
                  null,
                ),
                url: get(props, 'invoiceInfo.url', null),
              })
                ? {
                    ...get(props, 'invoiceInfo'),
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      props,
                      'invoiceInfo.invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(props, 'invoiceInfo.invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceDate', null),
                    receptionDate:
                      get(props, 'invoiceInfo.invoiceReceptionDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceReceptionDate', null),
                  }
                : undefined,
          }
        : get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            shouldSendNotificationInvoice:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isNil(get(props, 'receptionDate', null)) &&
              new Date(
                moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD'),
              ) >= new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(props, 'invoiceInfo.invoiceNumber', null),
                invoiceDate: get(props, 'invoiceInfo.invoiceDate', null),
                receptionDate: get(
                  props,
                  'invoiceInfo.invoiceReceptionDate',
                  null,
                ),
                url: get(props, 'invoiceInfo.url', null),
              })
                ? {
                    ...get(props, 'invoiceInfo'),
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(props, 'invoiceInfo.invoiceNumber'),
                    invoiceDate:
                      get(props, 'invoiceInfo.invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceDate', null),
                    receptionDate:
                      get(props, 'invoiceInfo.invoiceReceptionDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceReceptionDate', null),
                  }
                : undefined,
          }
        : undefined
      : withFeeNote
      ? {
          step: PROCEDURE_STEPS.PV,
          invoiceFeeNote:
            featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
            !isAllValuesIsNil({
              invoiceNumber: get(props, 'invoiceInfo.invoiceNumber', null),
              invoiceDate: get(props, 'invoiceInfo.invoiceDate', null),
              receptionDate: get(
                props,
                'invoiceInfo.invoiceReceptionDate',
                null,
              ),
              ...get(props, 'invoiceInfo'),
            })
              ? {
                  ...get(props, 'invoiceInfo'),
                  codeIntervenant: currentIntervenantStorage?.get()?.sagis_code,
                  createdBy: currentIntervenantStorage?.get()
                    ?.preferred_username,
                  invoiceNumber: get(props, 'invoiceInfo.invoiceNumber'),
                  invoiceDate:
                    get(props, 'invoiceInfo.invoiceDate', null) ===
                    'Invalid date'
                      ? null
                      : get(props, 'invoiceInfo.invoiceDate', null),
                  receptionDate:
                    get(props, 'invoiceInfo.invoiceReceptionDate', null) ===
                    'Invalid date'
                      ? null
                      : get(props, 'invoiceInfo.invoiceReceptionDate', null),
                }
              : undefined,
        }
      : undefined,
    additionalFees: get(props, 'isPreOpeningManager', false)
      ? !isNil(get(props.cost, 'costs', null)) &&
        get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  '.invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? moment().format('YYYY-MM-DD')
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
          }
        : !isNil(get(props.cost, 'costs', null)) &&
          get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
        ? {
            ...props.cost,
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? moment().format('YYYY-MM-DD')
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
            shouldSendNotificationInvoice:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isNil(get(props, 'receptionDate', null)) &&
              new Date(
                moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD'),
              ) >= new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')),
          }
        : undefined
      : undefined,
    jurisdictionDegreeId: 1,
    procedureNatureId: 3,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    decisionGenerationFeeNote: get(props, 'decisionGenerationFeeNote', false),
    pvHolder: get(props, 'isPreOpeningManager', false)
      ? !isNil(get(props, 'pvHolderType', null))
        ? {
            pvHolderTypeId: get(props, 'pvHolderType', null),
            other: get(props, 'otherPvHolder', null),
          }
        : {}
      : undefined,
    fraudInfo:
      featureService.hasFeature(FEATURES.FRAUD_INFO) &&
      claim[CLAIM.SUSPICIOUS] === CHOICE.YES
        ? {
            ...fraudInfo,
            intervenantCode: currentIntervenantStorage?.get()?.sagis_code,
          }
        : undefined,
    isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
    isCourtCalendarActive: featureService.hasFeature(FEATURES.COURT_CALENDAR),
  };
};

export const formatClaimToComplete = (
  props: any,
  priorityReasonClaimId: any,
) => {
  const {
    claim,
    victims,
    documents,
    driver,
    vehicule,
    opposingParty,
    fraudInfo,
  } = props.storedClaimToPreopen;
  const invoiceInfoAdditionelFees = props.invoiceInfoAdditionelFees;
  const updatePriorityVictim =
    !victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;
  let formattedDriver = driver;
  let formattedVehicule = [vehicule];

  if (driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.YES || isEmpty(driver)) {
    formattedDriver = {};
  }

  if (claim[CLAIM.HAS_MATERIAL_DAMAGES] === CHOICE.NO) {
    formattedVehicule = [];
  }

  if (claim[CLAIM.IS_JUDICIAL_PROCEDURE] === CHOICE.NO) {
    claim[CLAIM.JUDICIAL_PROCEDURE_ID] = null;
  }
  return {
    claim: {
      ...claim,
      [CLAIM.POLICY_NUMBER]: get(
        props.policyInfo,
        'sagis.policy.policyNumber',
        '',
      ),
      updatedBy: isLawyer()
        ? currentIntervenantStorage.get().preferred_username
        : currentUserStorage.get().username,
      [CLAIM.AUTHORITY_TYPE]: !isEmpty(get(claim, [CLAIM.AUTHORITY_TYPE]))
        ? get(claim, [CLAIM.AUTHORITY_TYPE])
        : null,
      [CLAIM.AUTHORITY_NAME]: !isEmpty(get(claim, [CLAIM.AUTHORITY_NAME]))
        ? get(claim, [CLAIM.AUTHORITY_NAME])
        : null,
      [CLAIM.OPENING_TYPE]: OPENING_TYPE.USAGE,
      [CLAIM.OPENING_STATE]: OPENING_STATE.COMPLETED,
      [CLAIM.STATUS]: CLAIM_STATUS.PENDING,
      [CLAIM.CIN]: get(props.policyInfo, 'sagis.policy.CIN', ''),
      fraudInfo:
        featureService.hasFeature(FEATURES.FRAUD_INFO) &&
        claim[CLAIM.SUSPICIOUS] === CHOICE.YES
          ? {
              ...fraudInfo,
              intervenantCode: currentIntervenantStorage?.get()?.sagis_code,
            }
          : undefined,
      [CLAIM.FIRM_REFERENCE]:
        get(props, 'isPreOpeningManager', false) &&
        get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
          ? get(props, 'firmReferenceLawyer', null)
          : get(claim, [CLAIM.FIRM_REFERENCE]),
      profilOpenner: get(props, 'isPreOpeningManager', false)
        ? PROFIL_OPENNER_GPO
        : PROFIL_OPENNER_LAWYER,
      completedBy: isLawyer()
        ? currentIntervenantStorage.get().preferred_username
        : currentUserStorage.get().username,
      completionDatePv: !isNil(get(props, 'completionDate', null))
        ? moment(get(props, 'completionDate', null)).format('YYYY-MM-DD')
        : null,
      receptionDatePv: !isNil(get(props, 'receptionDate', null))
        ? moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD')
        : null,
      priorityReasonId: addPriorityReasonProfession(
        null as any,
        !!victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
        props.listings,
      )
        ? addPriorityReasonProfession(
            null as any,
            !!victims.find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            props.listings,
          )
        : updatePriorityVictim
        ? null
        : undefined,
    },
    feeNote: get(props, 'isPreOpeningManager', false)
      ? get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            shouldSendNotificationInvoice:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isNil(get(props, 'receptionDate', null)) &&
              new Date(
                moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD'),
              ) >= new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(props, 'invoiceInfo.invoiceNumber', null),
                invoiceDate: get(props, 'invoiceInfo.invoiceDate', null),
                receptionDate: get(
                  props,
                  'invoiceInfo.invoiceReceptionDate',
                  null,
                ),
                url: get(props, 'invoiceInfo.url', null),
              })
                ? {
                    ...get(props, 'invoiceInfo'),
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(props, 'invoiceInfo.invoiceNumber'),
                    invoiceDate:
                      get(props, 'invoiceInfo.invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceDate', null),
                    receptionDate:
                      get(props, 'invoiceInfo.invoiceReceptionDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceReceptionDate', null),
                  }
                : undefined,
          }
        : {}
      : {},
    additionalFees: get(props, 'isPreOpeningManager', false)
      ? !isNil(get(props.cost, 'costs', null)) &&
        get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  '.invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? moment().format('YYYY-MM-DD')
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
          }
        : !isNil(get(props.cost, 'costs', null)) &&
          get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
        ? {
            ...props.cost,
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? moment().format('YYYY-MM-DD')
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
            shouldSendNotificationInvoice:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isNil(get(props, 'receptionDate', null)) &&
              new Date(
                moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD'),
              ) >= new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')),
          }
        : {}
      : {},
    victims: !isEmpty(victims)
      ? victims.map((vic: any) => {
          if (vic['injuryPostKey'] !== '2') vic.beneficiaries = [];

          vic.beneficiaries = vic.beneficiaries.map((el: any) => {
            return {
              ...el,
              [BENEFICIARY.FIRST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.FIRST_NAME, ''),
              ),
              [BENEFICIARY.LAST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.LAST_NAME, ''),
              ),
              [BENEFICIARY.FRAIS]: isNull(el[BENEFICIARY.FRAIS])
                ? null
                : Number(el[BENEFICIARY.FRAIS]),
            };
          });
          return {
            ...vic,
            claimId: claim.id,
            [VICTIM.ITT]: vic.itt === '' ? undefined : vic.itt,
            [VICTIM.ANNUAL_INCOME]:
              vic.annualOfIncome === '' ? undefined : vic.annualOfIncome,
            [VICTIM.IS_EXCLUDED]: vic[VICTIM.IS_EXCLUDED] === CHOICE.YES,
          };
        })
      : [],
    documents: !isEmpty(documents)
      ? documents.map((el: any) => ({
          ...el,
          claimId: claim.id,
        }))
      : documents,
    driver: formattedDriver,
    vehicules: featureService.hasFeature(FEATURES.OPPOSING_PARTY)
      ? opposingParty.vehicules
      : formattedVehicule,

    jurisdictionDegreeId: 1,
    procedureNatureId: 3,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    fraudInfo:
      featureService.hasFeature(FEATURES.FRAUD_INFO) &&
      claim[CLAIM.SUSPICIOUS] === CHOICE.YES
        ? {
            ...fraudInfo,
            intervenantCode: currentIntervenantStorage?.get()?.sagis_code,
          }
        : undefined,
    isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
    isCourtCalendarActive: featureService.hasFeature(FEATURES.COURT_CALENDAR),
  };
};

export const formatClaimToCompleteAndValidation = (
  props: any,
  priorityReasonClaimId: any,
) => {
  const {
    claim,
    victims,
    documents,
    driver,
    vehicule,
    opposingParty,
    fraudInfo,
  } = props.storedClaimToPreopen;
  const invoiceInfoAdditionelFees = props.invoiceInfoAdditionelFees;
  const updatePriorityVictim =
    !victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;
  let formattedDriver = driver;
  let formattedVehicule = [vehicule];
  const riskId =
    props.policyInfo &&
    get(resolveSagisClaim(props.policyInfo), 'offeredRisks', []).find(
      (element: { riskName: string }) => element.riskName === rcCoporeal,
    )?.riskId;
  const resp = find(
    get(props.listings, 'responsability'),
    (element: { ItemKey: any }) =>
      element.ItemKey === claim[VALIDATION_DECISION.RESPONSIBILITY],
  );
  const responsibility =
    claim[VALIDATION_DECISION.MANAGEMENT_MODE] === '2'
      ? claim[CLAIM.RESPONSABILITY_SHARE]
      : get(resp, 'ItemDescription', claim[CLAIM.RESPONSABILITY_SHARE]);

  if (driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.YES) {
    formattedDriver = {};
  }

  if (claim[CLAIM.HAS_MATERIAL_DAMAGES] === CHOICE.NO) {
    formattedVehicule = [];
  }

  if (claim[CLAIM.IS_JUDICIAL_PROCEDURE] === CHOICE.NO) {
    claim[CLAIM.JUDICIAL_PROCEDURE_ID] = null;
  }
  const formattedSagisVictims = victims
    .filter((v: any) => !v.isDeleted && !v.id)
    .map((victim: VictimType) => {
      const getDescriptionByCode = (code: number | string, list: any) => {
        const item = get(list, 'classVictim').find(
          (item: any) => Number(item.ItemKey) === Number(code),
        );
        return item ? item.ItemDescription : '';
      };
      let setDefaultBirthDate = false;
      if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
        setDefaultBirthDate = true;
      }
      const commonVictim: any = {
        victimFirstName: victim[VICTIM.FIRST_NAME],
        victimLastName: victim[VICTIM.LAST_NAME],
        victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
        injuryCode:
          String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
            ? victim[VICTIM.INJURY_POST_KEY]
            : 0,
        qualityVictim: victim[VICTIM.QUALITY_KEY],
        classVictim: victim[VICTIM.CLASS_KEY],
        classVictimDescription: getDescriptionByCode(
          victim[VICTIM.CLASS_KEY],
          props.listings,
        ),
        cspCode: get(
          get(props.listings, 'Profession', []).find(
            (el: any) => el.ItemKey === victim.professionCode,
          ),
          'ItemGroup',
          null,
        ),
        gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
        annualInCome:
          ((victim.annualOfIncome as unknown) as string) === ''
            ? undefined
            : victim.annualOfIncome,
        annualOfIncome:
          ((victim.annualOfIncome as unknown) as string) === ''
            ? null
            : victim.annualOfIncome,
        Town: victim[VICTIM.CITY],
        Complement: victim[VICTIM.ADDRESS],
        isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1,
        setDefaultBirthDate,
        itt: parseInt((victim[VICTIM.ITT] as unknown) as string),
        numberOfSpouse: victim[VICTIM.NUMBER_OF_SPOUSE],
      };
      if (victim.injuryPostKey === INJURY_POST_DECEASED) {
        victim.nextOfKins = victim.beneficiaries.map((el: any) => {
          return {
            fullName: `${removeWhiteSpaceFromString(
              el.firstName,
            )} ${removeWhiteSpaceFromString(el.lastName)}`,
            qualityCode: Number(el.beneficiaryType),
            age: el.age,
            birthDate: el?.dateOfBirth,
            funeralFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
              ? el.funeralFees
              : !isNil(el.funeralFees) && !!Number(responsibility)
              ? el.funeralFees / (Number(responsibility) / 100)
              : el.funeralFees,
            handicapIndicator: el.handicapIndicator ? 'Y' : 'N',
            dependentIndicator: el.dependentIndicator ? 'Y' : 'N',
          };
        });
        delete commonVictim.itt;
        delete (victim as Partial<VictimType>).itt;
      } else {
        delete (victim as Partial<VictimType>).beneficiaries;
      }

      return {
        ...victim,
        ...commonVictim,
        isLawyerInboxesActive: featureService.hasFeature(
          FEATURES.LAWYER_INBOXES,
        ),
      };
    });
  const formattedSagisVehiculesToUpdate = !isEmpty(
    get(opposingParty, 'vehicules', []),
  )
    ? get(opposingParty, 'vehicules', [])
        .filter((v: any) => v.isModified && v.id)
        .map((vehicule: any) => {
          return {
            ...vehicule,
            insuranceCompanyCode: vehicule?.opposingParty,
            partyKey: vehicule?.partyKey,
            riskID: riskId,
          };
        })
    : [];
  const formattedSagisVictimsToUpdate = victims
    .filter((v: any) => v.isModified && v.id)
    .map((victim: VictimType) => {
      let setDefaultBirthDate = false;
      if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
        setDefaultBirthDate = true;
      }
      return {
        ...victim,
        victimFirstName: victim[VICTIM.FIRST_NAME],
        victimLastName: victim[VICTIM.LAST_NAME],
        victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
        injuryCode:
          String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
            ? victim[VICTIM.INJURY_POST_KEY]
            : 0,
        qualityVictim: victim[VICTIM.QUALITY_KEY],
        classVictim: victim[VICTIM.CLASS_KEY],
        Town: victim[VICTIM.CITY],
        isExcluded: false,
        gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
        Complement: victim[VICTIM.ADDRESS],
        isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1,
        setDefaultBirthDate,
        cspCode: get(
          get(props.listings, 'Profession', []).find(
            (el: any) => el.ItemKey === victim.professionCode,
          ),
          'ItemGroup',
          null,
        ),
        itt: parseFloat((victim[VICTIM.ITT] as unknown) as string),
      };
    });
  return {
    claim: {
      ...claim,
      [CLAIM.POLICY_NUMBER]: get(
        props.policyInfo,
        'sagis.policy.policyNumber',
        '',
      ),
      updatedBy: isLawyer()
        ? currentIntervenantStorage.get().preferred_username
        : currentUserStorage.get().username,
      [CLAIM.AUTHORITY_TYPE]: !isEmpty(get(claim, [CLAIM.AUTHORITY_TYPE]))
        ? get(claim, [CLAIM.AUTHORITY_TYPE])
        : null,
      [CLAIM.AUTHORITY_NAME]: !isEmpty(get(claim, [CLAIM.AUTHORITY_NAME]))
        ? get(claim, [CLAIM.AUTHORITY_NAME])
        : null,
      [CLAIM.OPENING_TYPE]: OPENING_TYPE.USAGE,
      [CLAIM.OPENING_STATE]: OPENING_STATE.COMPLETED,
      [CLAIM.STATUS]: CLAIM_STATUS.VALIDATED,
      [CLAIM.CIN]: get(props.policyInfo, 'sagis.policy.CIN', ''),
      fraudInfo:
        featureService.hasFeature(FEATURES.FRAUD_INFO) &&
        claim[CLAIM.SUSPICIOUS] === CHOICE.YES
          ? {
              ...fraudInfo,
              intervenantCode: currentIntervenantStorage?.get()?.sagis_code,
            }
          : undefined,
      [CLAIM.FIRM_REFERENCE]:
        get(props, 'isPreOpeningManager', false) &&
        get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
          ? get(props, 'firmReferenceLawyer', null)
          : get(claim, [CLAIM.FIRM_REFERENCE]),
      profilOpenner: get(props, 'isPreOpeningManager', false)
        ? PROFIL_OPENNER_GPO
        : PROFIL_OPENNER_LAWYER,

      completionDatePv: !isNil(get(props, 'completionDate', null))
        ? moment(get(props, 'completionDate', null)).format('YYYY-MM-DD')
        : null,
      receptionDatePv: !isNil(get(props, 'receptionDate', null))
        ? moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD')
        : null,
      priorityReasonId: addPriorityReasonProfession(
        null as any,
        !!victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
        props.listings,
      )
        ? addPriorityReasonProfession(
            null as any,
            !!victims.find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            props.listings,
          )
        : updatePriorityVictim
        ? null
        : undefined,
    },
    feeNote:
      get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(props, 'invoiceInfo.invoiceNumber', null),
                invoiceDate: get(props, 'invoiceInfo.invoiceDate', null),
                receptionDate: get(
                  props,
                  'invoiceInfo.invoiceReceptionDate',
                  null,
                ),
                url: get(props, 'invoiceInfo.url', null),
              })
                ? {
                    ...get(props, 'invoiceInfo'),
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      props,
                      'invoiceInfo.invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(props, 'invoiceInfo.invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceDate', null),
                    receptionDate:
                      get(props, 'invoiceInfo.invoiceReceptionDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceReceptionDate', null),
                  }
                : undefined,
          }
        : get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            shouldSendNotificationInvoice:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isNil(get(props, 'receptionDate', null)) &&
              new Date(
                moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD'),
              ) >= new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(props, 'invoiceInfo.invoiceNumber', null),
                invoiceDate: get(props, 'invoiceInfo.invoiceDate', null),
                receptionDate: get(
                  props,
                  'invoiceInfo.invoiceReceptionDate',
                  null,
                ),
                url: get(props, 'invoiceInfo.url', null),
              })
                ? {
                    ...get(props, 'invoiceInfo'),
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(props, 'invoiceInfo.invoiceNumber'),
                    invoiceDate:
                      get(props, 'invoiceInfo.invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceDate', null),
                    receptionDate:
                      get(props, 'invoiceInfo.invoiceReceptionDate', null) ===
                      'Invalid date'
                        ? null
                        : get(props, 'invoiceInfo.invoiceReceptionDate', null),
                  }
                : {},
          }
        : {},
    additionalFees: get(props, 'isPreOpeningManager', false)
      ? !isNil(get(props.cost, 'costs', null)) &&
        get(props, 'pvHolderType', null) === HOLDER_PV_PROVIDER
        ? {
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  '.invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? moment().format('YYYY-MM-DD')
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
          }
        : !isNil(get(props.cost, 'costs', null)) &&
          get(props, 'pvHolderType', null) === HOLDER_PV_LAWYER
        ? {
            ...props.cost,
            step: PROCEDURE_STEPS.PV,
            codeIntervenant: get(props, 'providerTo'),
            beneficiary: get(
              props.intervenants?.find(
                (el: any) => el.ItemKey === get(props, 'providerTo'),
              ),
              'ItemDescription',
            ),
            invoiceFeeNote:
              featureService.hasFeature(FEATURES.LIMIT_INVOICE_DATE) &&
              !isAllValuesIsNil({
                invoiceNumber: get(
                  invoiceInfoAdditionelFees,
                  'invoiceNumber',
                  null,
                ),
                invoiceDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceDate',
                  null,
                ),
                receptionDate: get(
                  invoiceInfoAdditionelFees,
                  'invoiceReceptionDate',
                  null,
                ),
                url: get(invoiceInfoAdditionelFees, 'url', null),
              })
                ? {
                    ...invoiceInfoAdditionelFees,
                    codeIntervenant: get(props, 'providerTo'),
                    createdBy: currentUserStorage?.get()?.username,
                    invoiceNumber: get(
                      invoiceInfoAdditionelFees,
                      'invoiceNumber',
                      null,
                    ),
                    invoiceDate:
                      get(invoiceInfoAdditionelFees, 'invoiceDate', null) ===
                      'Invalid date'
                        ? null
                        : get(invoiceInfoAdditionelFees, 'invoiceDate', null),
                    receptionDate:
                      get(
                        invoiceInfoAdditionelFees,
                        'invoiceReceptionDate',
                        null,
                      ) === 'Invalid date'
                        ? moment().format('YYYY-MM-DD')
                        : get(
                            invoiceInfoAdditionelFees,
                            'invoiceReceptionDate',
                            null,
                          ),
                  }
                : undefined,
            shouldSendNotificationInvoice:
              featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
              !isNil(get(props, 'receptionDate', null)) &&
              new Date(
                moment(get(props, 'receptionDate', null)).format('YYYY-MM-DD'),
              ) >= new Date(moment(LIMIT_DATE_INVOICE).format('DD-MM-YYYY')),
          }
        : {}
      : {},
    victims: !isEmpty(victims)
      ? victims.map((vic: any) => {
          if (vic['injuryPostKey'] !== '2') vic.beneficiaries = [];

          vic.beneficiaries = vic.beneficiaries.map((el: any) => {
            return {
              ...el,
              [BENEFICIARY.FIRST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.FIRST_NAME, ''),
              ),
              [BENEFICIARY.LAST_NAME]: removeWhiteSpaceFromString(
                get(el, BENEFICIARY.LAST_NAME, ''),
              ),
              [BENEFICIARY.FRAIS]: isNull(el[BENEFICIARY.FRAIS])
                ? null
                : Number(el[BENEFICIARY.FRAIS]),
            };
          });
          return {
            ...vic,
            claimId: claim.id,
            [VICTIM.ITT]: vic.itt === '' ? undefined : vic.itt,
            [VICTIM.ANNUAL_INCOME]:
              vic.annualOfIncome === '' ? undefined : vic.annualOfIncome,
            [VICTIM.IS_EXCLUDED]: vic[VICTIM.IS_EXCLUDED] === CHOICE.YES,
          };
        })
      : [],
    documents: !isEmpty(documents)
      ? documents.map((el: any) => ({
          ...el,
          claimId: claim.id,
        }))
      : documents,
    driver: formattedDriver,
    vehicules: featureService.hasFeature(FEATURES.OPPOSING_PARTY)
      ? opposingParty.vehicules
      : formattedVehicule,

    jurisdictionDegreeId: 1,
    procedureNatureId: 3,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    victimsSagisToAdd: [...formattedSagisVictims],
    victimsSagisToUpdate: [...formattedSagisVictimsToUpdate],

    claimCircumstances: {
      scaleCaseCode: get(claim, 'scaleCaseKey', {}),
      circumstanceCode: get(claim, 'accidentCirconstanceKey', {}),
      responsabilityRate: get(claim, 'responsabilityKey', {}),
      managementMode: MANAGEMENT_MODES.COMMON_RIGHT,
      riskId,
      isFromCorporel: true,
    },
    fraudInfo:
      featureService.hasFeature(FEATURES.FRAUD_INFO) &&
      claim[CLAIM.SUSPICIOUS] === CHOICE.YES
        ? {
            ...fraudInfo,
            intervenantCode: currentIntervenantStorage?.get()?.sagis_code,
          }
        : undefined,
    isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
    isCourtCalendarActive: featureService.hasFeature(FEATURES.COURT_CALENDAR),
    opposingPartiesSagisToAdd: !isEmpty(get(opposingParty, 'vehicules', []))
      ? formatOpposingParties(
          opposingParty.vehicules.filter((v: any) => !v.isDeleted && !v.id),
          riskId,
          claim.claimNumber,
          claim.id as any,
          get(props.listings, 'compagnies', []),
        )
      : [],
    opposingPartiesSagisToUpdate: formattedSagisVehiculesToUpdate,
    riskKey: riskId,
    claimNumber: get(claim, 'claimNumber'),
  };
};

export const isCompanyIntervenant = (
  intervenantListings: any,
  sagisCode: any,
) => {
  return get(
    intervenantListings?.find((el: any) => el.ItemKey === sagisCode),
    'isCompany',
    false,
  );
};

export const isCompanyIntervenants = (
  intervenantListings: any,
  lawyersCode: any,
) => {
  return intervenantListings
    ?.filter((el: any) => lawyersCode.includes(el.ItemKey))
    .map((el: any) => get(el, 'isCompany', false))
    .includes(false);
};

export const extractIntervenant = (
  intervenantListings: any,
  assignedLawyersByJudiciaryProcess: any,
) => {
  if (
    assignedLawyersByJudiciaryProcess &&
    !isEmpty(assignedLawyersByJudiciaryProcess)
  ) {
    const formattedAssignementLawyer = assignedLawyersByJudiciaryProcess.map(
      (el: any) => el?.assignedLawyer,
    );
    return intervenantListings?.filter((el: any) =>
      formattedAssignementLawyer.includes(el.ItemKey),
    );
  } else {
    return [];
  }
};

export const formatIntervenants = (lawyersCode: any) => {
  return lawyersCode.map((el: any) => ({
    codeIntervenant: el.assignedLawyer,
    isCompany: true,
  }));
};

export const renderDescriptionDialog = (
  isOpeningManager: boolean,
  isCourtManager: boolean,
) => {
  if (isOpeningManager) {
    return {
      description: messages.openingManagerConfirmationDialogDescription,
      subDescription: messages.openingManagerConfirmationDialogSubDescription,
    };
  } else if (isCourtManager) {
    return {
      description: messages.courtManagerDialogDescription,
      subDescription: messages.courtManagerDialogSubDescription,
    };
  }
};

export const formatClaimToPreOpenCourtManager = (
  props: any,
  priorityReasonClaimId: number | null = null,
) => {
  const {
    claim,
    victims,
    documents,
    driver,
    vehicule,
    opposingParty,
  } = props.storedClaimToPreopen;
  const updatePriorityVictim =
    !victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)) &&
    priorityReasonClaimId === 5;
  let formattedDriver = driver;
  let formattedVehicule = [vehicule];

  if (driver[DRIVER.IS_DRIVER_THE_INSURED] === CHOICE.YES) {
    formattedDriver = {};
  }

  if (claim[CLAIM.HAS_MATERIAL_DAMAGES] === CHOICE.NO) {
    formattedVehicule = [];
  }

  if (claim[CLAIM.IS_JUDICIAL_PROCEDURE] === CHOICE.NO) {
    claim[CLAIM.JUDICIAL_PROCEDURE_ID] = null;
  }
  delete claim?.regulations;
  delete claim?.offeredRisks;
  delete claim?.beneficiaries;
  delete claim?.contractorFees;
  delete claim?.availableRisks;

  return {
    claim: {
      ...claim,
      [CLAIM.POLICY_NUMBER]: get(
        props.policyInfo,
        'sagis.policy.policyNumber',
        '',
      ),
      [CLAIM.CIN]: get(props.policyInfo, 'sagis.policy.CIN', ''),
      [CLAIM.STATUS]: CLAIM_STATUS.VALIDATED,
      [CLAIM.FIRM_REFERENCE]: get(claim, [CLAIM.FIRM_REFERENCE]),
      profilOpenner: PROFIL_OPENNER_GJ,
      codeLawyer: DEFAULT_LAWYER_CODE,
      createdBy: currentUserStorage?.get()?.username,
      decidedBy: currentUserStorage?.get()?.username,
      managementModeKey: get(claim, [CLAIM.MANAGEMENT_MODE]),
      decidedAt: moment().format(),
      [CLAIM.INTERVENANT_TYPE]: DEFAULT_INTERVENANT_CODE,
      priorityReasonId: addPriorityReasonProfession(
        null as any,
        !!victims.find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
        props.listings,
      )
        ? addPriorityReasonProfession(
            null as any,
            !!victims.find((v: any) =>
              PROFESSION_CODES.includes(v.professionCode),
            ),
            props.listings,
          )
        : updatePriorityVictim
        ? null
        : undefined,
    },
    victims: victims.map((vic: any) => {
      if (vic['injuryPostKey'] !== '2') vic.beneficiaries = [];

      vic.beneficiaries = vic.beneficiaries.map((el: any) => {
        return {
          ...el,
          [BENEFICIARY.FIRST_NAME]: removeWhiteSpaceFromString(
            get(el, BENEFICIARY.FIRST_NAME, ''),
          ),
          [BENEFICIARY.LAST_NAME]: removeWhiteSpaceFromString(
            get(el, BENEFICIARY.LAST_NAME, ''),
          ),
          [BENEFICIARY.FRAIS]: isNull(el[BENEFICIARY.FRAIS])
            ? null
            : Number(el[BENEFICIARY.FRAIS]),
        };
      });
      return {
        ...vic,
        [VICTIM.ITT]: vic.itt === '' ? undefined : vic.itt,
        [VICTIM.ANNUAL_INCOME]:
          vic.annualOfIncome === '' ? undefined : vic.annualOfIncome,
        [VICTIM.IS_EXCLUDED]: vic[VICTIM.IS_EXCLUDED] === CHOICE.YES,
      };
    }),
    documents,
    driver: formattedDriver,
    vehicules: featureService.hasFeature(FEATURES.OPPOSING_PARTY)
      ? opposingParty.vehicules
      : formattedVehicule,
    jurisdictionDegreeId: 1,
    procedureNatureId: 3,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    isLawyerInboxesActive: featureService.hasFeature(FEATURES.LAWYER_INBOXES),
  };
};

export const constructHomePath = (
  isOpeningManager: boolean,
  isPreOpeningManager: boolean,
  isCourtManager: boolean,
) => {
  if (isOpeningManager) {
    return pushTo(PAGES.MANAGER_HOME)();
  } else if (isPreOpeningManager) {
    return pushTo(PAGES.PREOPEN_MANAGER_HOME)();
  } else if (isCourtManager) {
    return pushTo(PAGES.COURT_MANAGER_HOME)();
  } else {
    return pushTo(PAGES.LAWYER_HOME)();
  }
};
